import {Box, Typography} from '@mui/material';
import {FC, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import ArrDataGridPro from '../../common/arr-data-grid-pro/ArrDataGridPro';
import './DocExplorerDataGrid.scss';
import {
  DataGridRowSelectionModel,
  DocExplorerColumnConfig,
  DocExplorerColumnHeader,
  DocTableRow,
} from '../../../shared/interfaces';

import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {useGetDocExplorerGridDataQuery} from '../../../hooks/document/useDocument';
import FilterTextField from '../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {t} from 'i18next';
import FilterDateField from '../../common/arr-data-grid-pro/filter-components/date-time-field/FilterDateField';
import {convertToUTCDate} from '../../../utils/formatters';
import CellWrapper from '../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {deepClone} from '@mui/x-data-grid/internals';
import {
  ActiveFilter,
  filterSelectionType,
} from '../doc-filter/doc-filter-accordion/DocExplorerFilterAccordion';
import {ActiveFilterPayload} from '../../../api/types/document.type';

import DocPreviewModal from '../../document-view-prizmdoc/DocPreviewModal';
import ArrButton from '../../common/arr-button/ArrButton';
import {isEmpty} from 'lodash';

export interface DocForPreview {
  id: number;
  docId: string;
  docName?: string;
  docExt?: string;
}

type DocExplorerDataGridProps = {
  selectedNodeID?: string;
  columnConfig: DocExplorerColumnConfig | undefined;
  selectedIds?: string[];
  unselectedIds?: string[];
  isAllSelected?: boolean;
  activeFilters: ActiveFilter[];
  columnResize?: boolean;
  autoColumnSize?: boolean;
  onColumnOrderChange: (arg0: GridColumnOrderChangeParams) => void;
  onSelectionStateChange: (arg0: DataGridRowSelectionModel) => void;
  setEnableActionButton?: (val: boolean) => void;
  onColumnFiltersChange?: (
    filters: {columnName: string; filterValue: string}[]
  ) => void;
};

const ROW_HEIGHT = 36;
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FIXED_MIN_COLUMN_WIDTH = 200;
const FIXED_ADJUSTMENT_WIDTH = 62;
const COL_MIN_WIDTH = 80;
const Family = {
  Parent: 'P',
  Child: 'C',
  Standalone: 'S',
  ParentChild: 'PC',
};

const DocExplorerDataGrid: FC<DocExplorerDataGridProps> = ({
  selectedNodeID = '',
  columnConfig,
  selectedIds,
  unselectedIds,
  isAllSelected,
  activeFilters,
  columnResize = false,
  autoColumnSize = false,
  onColumnOrderChange,
  onSelectionStateChange,
  setEnableActionButton,
  onColumnFiltersChange,
}) => {
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'DocID', sort: 'asc'},
  ]);

  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [selectedDocForPreview, setSelectedDocForPreview] =
    useState<DocForPreview | null>(null);

  const dataGridContainerRef = useRef<HTMLDivElement>(null);
  const [dataGridContainerWidth, setDataGridContainerWidth] =
    useState<number>(0);

  const {
    mutate: getDocGridData,
    data: docGridData,
    isPending: isDocGridDataFetching,
  } = useGetDocExplorerGridDataQuery();

  const getAutoSizeWidth = (column: DocExplorerColumnHeader) => {
    const MAX_WIDTH = 500;
    const ROW_ITEMS_TO_CONSIDER = 20;
    const AVG_LETTER_WIDTH = 12;

    let longestString = ''.padEnd(column.docExplorerColMinWidth, '-');
    if (!isEmpty(docGridData) && column?.docExplorerColCode) {
      const rowData: DocTableRow[] = docGridData.data.slice(
        0,
        ROW_ITEMS_TO_CONSIDER
      );

      longestString = rowData.reduce((longest, col) => {
        const current = docDataToTableRow(col)[column.docExplorerColCode] ?? '';
        return current.length > longest.length ? current : longest;
      }, '-');
    }

    const relativeWidth =
      longestString.length > column.docExplorerColTitle.length
        ? longestString.length * AVG_LETTER_WIDTH
        : column.docExplorerColTitle.length * AVG_LETTER_WIDTH;

    return relativeWidth <= MAX_WIDTH ? relativeWidth : MAX_WIDTH;
  };

  const getDocExplorerColumns = (
    columnConfig: DocExplorerColumnHeader[]
  ): GridColDef[] => {
    const columns: GridColDef[] = [];
    const visibleColumns = columnConfig.filter((column) => column.isVisible);

    // dynamically calculated column width start
    const minTotalWidth = visibleColumns.length * FIXED_MIN_COLUMN_WIDTH;
    const adjustedContainerWidth =
      dataGridContainerWidth - FIXED_ADJUSTMENT_WIDTH;

    let additionalWidth = 0;
    if (minTotalWidth < adjustedContainerWidth)
      additionalWidth = adjustedContainerWidth - minTotalWidth;

    const totalRealtiveWidth = visibleColumns.reduce(
      (acc, current) => acc + current.docExplorerColMinWidth,
      0
    );
    // dynamically calculated column width end

    useEffect(() => {
      const columnFilters =
        filterModel.items
          .filter((item) => item.value && item.field)
          .map((item) => ({
            columnName: item.field as string,
            filterValue: String(item.value?.toString().trim()),
          })) ?? [];
      onColumnFiltersChange?.(columnFilters);
    }, [filterModel]);

    visibleColumns.forEach((column, index) => {
      const columnWidth =
        FIXED_MIN_COLUMN_WIDTH +
        (additionalWidth / totalRealtiveWidth) * column.docExplorerColMinWidth;

      const autoColWidth =
        additionalWidth > 0 ? columnWidth : getAutoSizeWidth(column);

      const MIN_DEFAULT_VAL = autoColumnSize ? autoColWidth : columnWidth;

      columns.push({
        headerClassName: `${visibleColumns.length - 1 == index ? 'last-column' : ''}`,
        field: column.docExplorerColCode,
        headerName: column.docExplorerColTitle,
        sortable: column.canSort,
        filterable: true,
        resizable: columnResize,
        minWidth:
          MIN_DEFAULT_VAL < COL_MIN_WIDTH ? COL_MIN_WIDTH : MIN_DEFAULT_VAL,
        width: MIN_DEFAULT_VAL,
        renderCell: (params) => {
          let cellContent: ReactNode;
          let tooltipContent = params.value;
          let alwaysShowToolTip = false;
          if (column.docExplorerColCode == 'DocID') {
            cellContent = (
              <ArrButton
                onClick={() => {
                  setSelectedDocForPreview({
                    id: params.row.DocumentID,
                    docId: params.row.DocID,
                    docName: params.row.DocFileName,
                  });
                }}
                variant="text"
                size="xs"
                className="id-cell-preview"
                dataTestId={`doc-explorer-data-grid-${column.docExplorerColCode}-${params.id}-button`}
              >
                <Typography
                  className="doc-id-cell cell-content typo-text body3_size"
                  data-testid={`doc-explorer-data-grid-typography-${column.docExplorerColCode}-${params.id}`}
                >
                  {params.value}
                </Typography>
              </ArrButton>
            );
          } else if (column.docExplorerColDataTypeCode == 'DATETIME') {
            cellContent = (
              <Typography
                className="cell-content typo-text body3_size"
                data-testid={`doc-explorer-data-grid-typography-date-time-${column.docExplorerColCode}-${params.id}`}
              >
                {params.value ? convertToUTCDate(params.value) : ''}
              </Typography>
            );
            tooltipContent = convertToUTCDate(params.value);
          } else {
            cellContent = (
              <Typography
                className=" cell-content typo-text body3_size"
                data-testid={`doc-explorer-data-grid-typography-default-${column.docExplorerColCode}-${params.id}`}
              >
                {params.value}
              </Typography>
            );
            if (params.field == 'Family') {
              tooltipContent =
                params.value === Family.Standalone
                  ? t('docExplorer.docDataGrid.family.standalone')
                  : params.value === Family.Parent
                    ? t('docExplorer.docDataGrid.family.parent')
                    : params.value === Family.Child
                      ? t('docExplorer.docDataGrid.family.child')
                      : params.value;
              alwaysShowToolTip = true;
            }
          }
          return (
            <CellWrapper
              toolTipContent={tooltipContent}
              dataTestId={`cell-wrapper-tooltip-content-${params.value}`}
              cellParams={params}
              alwaysShowToolTip={alwaysShowToolTip}
            >
              {cellContent}
            </CellWrapper>
          );
        },
        renderHeaderFilter: (params) => {
          if (column.docExplorerColDataTypeCode == 'DATETIME') {
            return (
              <FilterDateField
                dataTestId={`filter-field-${index}`}
                gridRenderHeaderFilterProps={params}
                placeholder={t('common.datePlaceholder')}
              />
            );
          }
          return (
            <FilterTextField
              dataTestId={column.docExplorerColTitle}
              gridRenderHeaderFilterProps={params}
              placeholder={t('common.filter')}
              className="custom-filter"
            />
          );
        },
      });
    });
    return columns;
  };
  const prepareActiveFilterPayload = (filtersData: ActiveFilter[]) => {
    return filtersData.map((item) => {
      const {id, label, selectionType, values, type, endDate, startDate} = item;
      let operand: filterSelectionType;
      if (selectionType === 'include') {
        operand = 'IN';
      } else if (selectionType === 'exclude') {
        operand = 'NOTIN';
      } else {
        operand = selectionType || 'IN';
      }
      return {
        ProjectFieldId: id,
        ProjectFieldName: label,
        operand,
        values:
          type != 'DATETIME'
            ? values?.map((i) => i?.text)
            : [startDate, endDate],
        type,
      };
    });
  };

  const prepareDocTableRequest = (
    defaultPageNumber?: number
  ): DocTableRequest => {
    // Map column filters from filterModel
    const columnFilters =
      filterModel.items
        .filter((item) => item.value && item.field)
        .map((item) => ({
          columnName: item.field,
          filterValue: String(item.value.toString().trim()),
        })) ?? [];

    // Extract sorting info
    const sortingColumn = sortModel[0]?.field || 'DocID';
    const direction = sortModel[0]?.sort || 'asc';

    return {
      selectedNodeID,
      filters: prepareActiveFilterPayload(deepClone(activeFilters)),
      columnFilters,
      isFirstLoad: false,
      displayLength: pageSize,
      pageNumber: defaultPageNumber ?? pageNumber,
      sortingColumn,
      direction: direction,
    };
  };

  const handleSelectionStateChange = (data: DataGridRowSelectionModel) => {
    setEnableActionButton?.(data.isAllSelected || data.selectedIds.length > 0);
    onSelectionStateChange(data);
  };

  useEffect(() => {
    setPageNumber(1);
    getDocGridData(prepareDocTableRequest(1));
  }, [pageSize, filterModel, sortModel, selectedNodeID, activeFilters]);
  useEffect(() => {
    if (!isDocGridDataFetching) getDocGridData(prepareDocTableRequest());
  }, [pageNumber]);

  useEffect(() => {
    onSelectionStateChange({
      isAllSelected: false,
      selectedIds: [],
      unselectedIds: [],
    });
  }, [filterModel]);
  useEffect(() => {
    if (!dataGridContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDataGridContainerWidth((prev) => {
          if (Math.abs(prev - entry.contentRect.width) < 1) return prev; // prevent flickering
          return entry.contentRect.width;
        });
      }
    });
    observer.observe(dataGridContainerRef.current);

    return () => observer.disconnect();
  }, []);

  const data = useMemo(
    () => docGridData?.data.map(docDataToTableRow) ?? [],
    [docGridData, docDataToTableRow]
  );

  const columnFormatted = getDocExplorerColumns(
    columnConfig?.userProfileColumnHeaders ?? []
  )?.map((col) => {
    if (col.field === 'FileType') {
      return {...col, field: 'DocFileType'};
    } else if (col.field === 'FileName') {
      return {...col, field: 'DocFileName'};
    } else if (col.field === 'FamilyDatetime') {
      return {...col, field: 'MasterDate'};
    }
    return col;
  });

  return (
    <Box ref={dataGridContainerRef} className="doc-explorer-data-grid">
      <ArrDataGridPro
        rowHeight={ROW_HEIGHT}
        data-testid="doc-explorer-data-grid"
        primaryKey="DocumentID"
        columns={columnFormatted}
        onColumnOrderChange={onColumnOrderChange}
        onSelectionStateChange={handleSelectionStateChange}
        currentPage={pageNumber}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        rowCount={docGridData?.totalRecords ?? 0}
        showPerPageOptions
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        onPageChange={setPageNumber}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        rows={data}
        pagination
        disableColumnFilter
        disableColumnResize={!columnResize}
        filterModel={filterModel}
        sortModel={sortModel}
        onFilterModelChange={setFilterModel}
        onSortModelChange={setSortModel}
        headerFilters
        enableCheckbox
        selectedIds={selectedIds}
        unselectedIds={unselectedIds}
        isAllSelected={isAllSelected}
        disableVirtualization
        loading={isDocGridDataFetching}
        noRowsOverlayCustomText={t(
          'docExplorer.docDataGrid.noRowsOverlayCustomText'
        )}
        entityType={t('docExplorer.docDataGrid.entries')}
        getRowClassName={(params) => {
          const {row, indexRelativeToCurrentPage} = params;

          const classes: string[] = [];
          if (
            row.Family === Family.Parent ||
            row.Family === Family.Child ||
            row.Family === Family.ParentChild
          ) {
            classes.push('row-family');
            if (row.Family === Family.Child) {
              if (indexRelativeToCurrentPage !== pageSize - 1) {
                const nextRow = data[indexRelativeToCurrentPage + 1];

                if (nextRow) {
                  if (nextRow.Family !== Family.Child) {
                    classes.push('row-separator');
                  } else if (nextRow.FamilyGroupId !== row.FamilyGroupId) {
                    classes.push('row-separator');
                  }
                }
              }
            }
            if (row.Family === Family.Parent) {
              if (indexRelativeToCurrentPage !== pageSize - 1) {
                const nextRow = data[indexRelativeToCurrentPage + 1];

                if (nextRow) {
                  if (nextRow.Family === Family.Parent) {
                    classes.push('row-separator');
                  } else if (nextRow.FamilyGroupId !== row.FamilyGroupId) {
                    classes.push('row-separator');
                  }
                }
              }
            }
          }

          return classes.join(' ');
        }}
      />
      {!!selectedDocForPreview && (
        <DocPreviewModal
          showModal={!!selectedDocForPreview}
          onClose={() => setSelectedDocForPreview(null)}
          document={selectedDocForPreview}
        ></DocPreviewModal>
      )}
    </Box>
  );
};

export default DocExplorerDataGrid;

export interface DocTableRequest {
  selectedNodeID: string;
  filters: ActiveFilterPayload[];
  columnFilters: {
    columnName: string;
    filterValue: string;
  }[];
  isFirstLoad: boolean;
  displayLength: number;
  pageNumber: number;
  sortingColumn: string;
  direction: string;
}

const docDataToTableRow = (docData: DocTableRow) => {
  return {
    DocID: docData.docID,
    DocumentID: docData.documentID,
    ProdBeginningBates: docData.prodBeginningBates,
    External_Begin_Doc: docData.externalBeginDoc,
    Family: docData.family,
    CustodianName: docData.custodianName,
    DocFileType: docData.docType,
    DocFileName: docData.docName,
    MasterDate: docData.masterDate,
    EmailAuthorName: docData.emailAuthor,
    EmailRecipientNames: docData.emailRecipients,
    ProdVolume: docData.prodVolume,
    FamilyGroupId: docData.familyGroupId,
  };
};
